import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, RotateCcw, PackageCheck, PlayCircle, Truck, MapPin, Send, CheckCircle2, Zap, FileText, AlertTriangle, ListChecks, Zap as QuickIcon, History, ClipboardList, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { EmailHealthCard } from "@/components/admin/EmailsAdmin";
import CertificateOrdersSummary from "@/components/admin/CertificateOrdersSummary";

// Feb-2026 · Sales-workflow shortcuts on the admin Today home. All
// point to EXISTING canonical routes — no new pages, no new report.
const SALES_SHORTCUTS = [
  { key: "quick_sale",       label: "Quick Sale",       to: "/admin/quick-sale",
    Icon: QuickIcon,       tone: "bg-red-600 text-white border-red-900",
    hint: "In-store fast POS"     },
  { key: "historical_sale",  label: "Historical Sale",  to: "/admin/historical-entry",
    Icon: History,         tone: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    hint: "Back-fill past sales"  },
  { key: "sales_orders",     label: "Sales Orders",     to: "/admin/sales-orders",
    Icon: ClipboardList,   tone: "bg-blue-100 text-blue-900 border-blue-900",
    hint: "WhatsApp · manual · shipping" },
  { key: "sales_report",     label: "Sales Report",     to: "/admin/sales-dashboard",
    Icon: BarChart3,       tone: "bg-emerald-100 text-emerald-900 border-emerald-900",
    hint: "Unified sales dashboard" },
];

const ICONS = {
  awaiting_payment:    { icon: Send,          tone: "yellow" },
  payment_submitted:   { icon: PackageCheck,  tone: "blue"   },
  payment_verified:    { icon: PlayCircle,    tone: "green"  },
  preparing:           { icon: ShoppingBag,   tone: "yellow" },
  ready_pickup:        { icon: MapPin,        tone: "emerald"},
  ready_ship:          { icon: Truck,         tone: "emerald"},
  ready_unknown:       { icon: AlertTriangle, tone: "red"    },
  stale_orders:        { icon: AlertTriangle, tone: "red"    },
  missing_information: { icon: AlertTriangle, tone: "red"    },
  completed_today:     { icon: CheckCircle2,  tone: "gray"   },
  active_sessions:     { icon: Zap,           tone: "red"    },
};

const TONE_CLS = {
  yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
  blue:   "bg-blue-100 text-blue-900 border-blue-900",
  green:  "bg-emerald-50 text-emerald-900 border-emerald-900",
  red:    "bg-red-50 text-red-900 border-red-900",
  gray:   "bg-neutral-100 text-neutral-800 border-neutral-500",
  emerald:"bg-emerald-100 text-emerald-900 border-emerald-900",
};

export default function HomeDashboard({ onNavigateToFulfillment, onNavigateToTab }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/home-dashboard");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Home dashboard failed.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-12 text-center"><Loader2 className="animate-spin inline" /> Loading today&apos;s queue…</div>;
  if (!data) return null;

  const cards = data.cards || [];
  const openCard = (c) => {
    if (c.key === "active_sessions") { onNavigateToFulfillment?.("all"); return; }
    // Phase C — stale + missing-info click-throughs.
    if (c.key === "stale_orders") { nav("/admin/orders?preset=all_time&view=stale"); return; }
    if (c.key === "missing_information") { nav("/admin/orders?preset=all_time&view=missing_information"); return; }
    // Phase B — Order Overview cards → All Orders hub with matching filter.
    if (c.section === "order_overview") {
      const params = new URLSearchParams({ preset: "all_time" });
      if (c.view) params.set("view", c.view);
      if (c.status) params.set("status", c.status);
      if (c.fulfillment_group) params.set("fulfillment_group", c.fulfillment_group);
      nav(`/admin/orders?${params.toString()}`);
      return;
    }
    // Today · Completed today → All Orders filtered on completion date.
    if (c.key === "completed_today") {
      nav("/admin/orders?preset=today&date_basis=completion&status=completed");
      return;
    }
    if (c.key === "new_orders_today") {
      nav("/admin/orders?preset=today&date_basis=created"); return;
    }
    if (c.key === "paid_today") {
      nav("/admin/orders?preset=today&date_basis=payment"); return;
    }
    if (c.key === "cancelled_today") {
      nav("/admin/orders?preset=today&status=cancelled"); return;
    }
    if (c.key === "quotes_today") { nav("/admin/quotes"); return; }
    if (c.value === 0) return;
    // Legacy Needs-Attention click-through → operational fulfillment queue.
    onNavigateToFulfillment?.(c.status || "all", c.fulfillment_group || null);
  };

  // Group cards by section for Section A/B/C/D layout.
  const sections = {
    needs_attention: { title: "Needs Attention", kicker: "kicker-red" },
    today:           { title: "Today",           kicker: "kicker-blue" },
    order_overview:  { title: "Order Overview",  kicker: "kicker-green",
                       subtitle: "All order records · every status, every time (includes drafts, cancelled and test rows). This does NOT match the Sales dashboard \"Orders\" tile — see the Sales page for revenue-eligible orders in a selected reporting window." },
    system_health:   { title: "System Health",   kicker: "kicker-gray" },
  };
  const grouped = { needs_attention: [], today: [], order_overview: [], system_health: [] };
  for (const c of cards) {
    const sec = c.section || "needs_attention";
    if (grouped[sec]) grouped[sec].push(c);
  }

  return (
    <div className="space-y-6" data-testid="home-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="kicker kicker-red">Today</div>
          <h2 className="font-display text-3xl">Admin Operations</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">Where should you spend the next hour?</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/admin/sales")} className="btn-ghost text-sm" data-testid="dashboard-open-sales-tax">
            Open Sales &amp; Tax Reporting
          </button>
          <button onClick={load} className="btn-ghost text-sm" data-testid="dashboard-refresh">
            <RotateCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Sales workflow shortcuts — 4 prominent tiles linking to
          existing routes. Not a new page; not a new report. */}
      <div data-testid="dashboard-section-sales-shortcuts">
        <div className="kicker kicker-red">Sales Workflows</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          {SALES_SHORTCUTS.map((s) => (
            <button key={s.key}
                    onClick={() => nav(s.to)}
                    data-testid={`dashboard-sales-shortcut-${s.key}`}
                    className={`text-left p-4 border-2 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)] ${s.tone}`}>
              <div className="flex items-center justify-between">
                <s.Icon size={20} />
              </div>
              <div className="mt-3 font-display text-xl leading-tight">{s.label}</div>
              <div className="mt-1 text-[11px] uppercase tracking-widest font-bold opacity-80">{s.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {Object.entries(sections).map(([key, meta]) => {
        const list = grouped[key] || [];
        if (!list.length) return null;
        return (
          <div key={key} data-testid={`dashboard-section-${key}`}>
            <div className={`kicker ${meta.kicker}`}>{meta.title}</div>
            {meta.subtitle && (
              <div className="text-[11px] text-[var(--smoke)] italic mt-1 max-w-3xl"
                   data-testid={`dashboard-section-${key}-subtitle`}>
                {meta.subtitle}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-2">
              {list.map((c) => {
                if (c.key === "ready_unknown" && (c.value || 0) === 0) return null;
                const iconMeta = ICONS[c.key] || {};
                const Icon = iconMeta.icon || Send;
                const tone = TONE_CLS[iconMeta.tone] || TONE_CLS.gray;
                return (
                  <button
                    key={c.key}
                    onClick={() => openCard(c)}
                    className={`text-left p-4 border-2 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)] ${tone} ${c.urgent ? "ring-4 ring-red-600 ring-offset-2 ring-offset-[var(--paper)]" : ""}`}
                    data-testid={`home-card-${c.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={18} />
                      {c.urgent && <span className="text-[10px] uppercase tracking-widest font-bold bg-red-700 text-white px-1.5 py-0.5 rounded">{c.key === "ready_unknown" ? "Review" : "Action"}</span>}
                    </div>
                    <div className="mt-3 font-display text-4xl leading-none">{c.value.toLocaleString()}</div>
                    <div className="mt-1 text-xs uppercase tracking-widest font-bold opacity-80">{c.label}</div>
                  </button>
                );
              })}
              {key === "system_health" && <EmailHealthCard onOpen={() => onNavigateToTab?.("emails")} />}
            </div>
          </div>
        );
      })}

      {/* Certificate Orders summary — dedicated section fed by a single
          admin aggregation endpoint. Six clickable metric cards deep-link
          into the Certificate Orders page with the matching ?filter=…
          already applied. Runs independently of the fulfillment
          `home-dashboard` data. */}
      <CertificateOrdersSummary />
    </div>
  );
}


function QuotesTodayCard({ onOpen }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/quotes/today-count")
      .then((r) => { if (!cancelled) setN(Number(r.data?.today || 0)); })
      .catch(() => { if (!cancelled) setN(0); });
    return () => { cancelled = true; };
  }, []);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left p-4 border-2 border-neutral-500 bg-neutral-100 text-neutral-800 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)]"
      data-testid="home-card-quotes_today"
    >
      <div className="flex items-center justify-between">
        <FileText size={18} />
      </div>
      <div className="mt-3 font-display text-4xl leading-none">
        {n === null ? <Loader2 className="animate-spin inline" size={20} /> : n}
      </div>
      <div className="mt-1 text-xs uppercase tracking-widest font-bold opacity-80">Quotes today</div>
    </button>
  );
}
